/**
 * Motor de jornadas diárias (T12).
 *
 * Agrupa marcações por colaborador + data operacional, para pares entry/exit,
 * calcula minutos trabalhados e esperados, e sinaliza inconsistências.
 *
 * Privacidade: usa apenas IDs anônimos. Nenhum CPF/nome circula aqui.
 */

import type {
  CanonicalDataset,
  DiagnosticAssumptions,
  DailyJourney,
  PunchPair,
  JourneyIssueSignal,
  CanonicalSchedule,
  CanonicalPunch,
  LocalDate,
} from '../domain/canonical.types';
import type { MinuteDelta } from './metrics.types';
import { diffInMinutes, weekdayOf, dateOf } from '../domain/dates';
import { isNationalHoliday } from '../domain/holidays';

// =========================================================================
// Helpers internos
// =========================================================================

/**
 * Limite de gap (minutos) que separa sessões consecutivas.
 *
 * Princípio: gaps DENTRO de uma jornada (almoço, café, pausa entre
 * batidas) raramente passam de algumas horas; gaps ENTRE jornadas
 * (final de turno até início do próximo) são tipicamente ≥ 14h.
 *
 * **Threshold = 14h** (compara com `>` estrito):
 *
 * Por que 14h e não 12h: plantões 12×36 muitas vezes esticam um pouco
 * além de 12h (ex.: entrada 19:00 → saída 07:30 = 12h30, com pequena
 * hora extra). Com threshold de 12h, esses turnos splitavam em duas
 * sessões imbalanced — entrada solitária no sábado, saída solitária
 * no domingo —, gerando spike artificial de "correções necessárias"
 * em fins de semana onde rola plantão. 14h dá folga pra hora extra
 * dentro do turno sem quebrar a sessão.
 *
 * Casos típicos:
 * - **Plantão 12×36 com 2 batidas (gap 12h-14h)**: mesma sessão ✓
 *   (turno legítimo, mesmo com hora extra).
 * - **Trabalhador noturno (entrada 19:57, saída 07:31 com almoço 1h)**:
 *   maior gap interno = ~5h33 — bem abaixo.
 * - **Day worker fim-de-turno → próximo dia (17h hoje → 07h amanhã)**:
 *   gap = 14h00 — não excede 14h, MAS o `MAX_SESSION_SPAN_MINUTES`
 *   (16h) limita o span total a partir do primeiro punch, garantindo
 *   que dias separados não se misturem.
 * - **Day worker fim-de-turno → próximo dia (17h hoje → 08h amanhã)**:
 *   gap = 15h > 14h → splits ✓.
 */
const SESSION_GAP_THRESHOLD_MINUTES = 14 * 60;

/**
 * Limite máximo de SPAN de uma sessão (do primeiro ao último punch).
 *
 * Reforço de segurança: mesmo que gaps individuais nunca passem do
 * limite acima, se o span total da sessão estourar 16h é certeza de
 * estar bundlando coisas que não deveriam (ex: um worker com punches
 * a cada 8-10h durante 4 dias seguidos viraria UMA sessão de 96h).
 *
 * Quando o span estoura 16h, força uma nova sessão a partir do punch
 * atual. 16h cobre o pior caso legítimo: jornada que começa às 06h
 * e termina às 22h (16h span) com vários breaks no meio.
 */
const MAX_SESSION_SPAN_MINUTES = 16 * 60;

/**
 * Sessão de jornada: conjunto de marcações de UM colaborador que
 * pertencem ao MESMO turno de trabalho, mesmo que cruzem meia-noite.
 *
 * `operationalDate` é a DATA CALENDÁRIO da PRIMEIRA marcação da sessão —
 * convenção brasileira ("turno da segunda-feira" = turno que iniciou na
 * segunda, mesmo terminando na terça de madrugada).
 */
export interface JourneySession {
  employeeId: string;
  operationalDate: LocalDate;
  punches: CanonicalPunch[];
}

/**
 * Detecta sessões de trabalho a partir das marcações brutas, agrupando
 * por colaborador e separando em sessões distintas quando o gap entre
 * marcações consecutivas excede `SESSION_GAP_THRESHOLD_MINUTES`.
 *
 * Substitui o agrupamento anterior por `(employeeId, punch.operationalDate)`
 * que dependia do `resolveOperationalDate` com cutoff fixo (4h) — esse
 * cutoff dividia turnos noturnos cuja saída acontece após 4h da manhã.
 *
 * Algoritmo:
 * 1. Agrupa por colaborador.
 * 2. Ordena por dateTime crescente.
 * 3. Walk: gap > limite → nova sessão; gap ≤ limite → mesma sessão.
 */
export function detectJourneySessions(
  punches: CanonicalPunch[],
  gapThresholdMinutes = SESSION_GAP_THRESHOLD_MINUTES,
): JourneySession[] {
  if (punches.length === 0) return [];

  // Agrupa por colaborador
  const byEmployee = new Map<string, CanonicalPunch[]>();
  for (const p of punches) {
    let arr = byEmployee.get(p.employeeId);
    if (arr === undefined) {
      arr = [];
      byEmployee.set(p.employeeId, arr);
    }
    arr.push(p);
  }

  const sessions: JourneySession[] = [];

  for (const [employeeId, empPunches] of byEmployee) {
    const sorted = [...empPunches].sort((a, b) =>
      a.dateTime < b.dateTime ? -1 : a.dateTime > b.dateTime ? 1 : 0,
    );

    let currentBucket: CanonicalPunch[] = [sorted[0]!];
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1]!;
      const cur = sorted[i]!;
      const gap = diffInMinutes(cur.dateTime, prev.dateTime);
      const spanFromStart = diffInMinutes(
        cur.dateTime,
        currentBucket[0]!.dateTime,
      );
      // Nova sessão quando: (a) gap > 11h (interjornada CLT) OU
      // (b) span da sessão excederia 16h (proteção contra bundling
      // espúrio em datasets longos com pequenos gaps consecutivos).
      if (gap > gapThresholdMinutes || spanFromStart > MAX_SESSION_SPAN_MINUTES) {
        sessions.push({
          employeeId,
          operationalDate: dateOf(currentBucket[0]!.dateTime),
          punches: currentBucket,
        });
        currentBucket = [cur];
      } else {
        currentBucket.push(cur);
      }
    }
    // Flush último bucket
    if (currentBucket.length > 0) {
      sessions.push({
        employeeId,
        operationalDate: dateOf(currentBucket[0]!.dateTime),
        punches: currentBucket,
      });
    }
  }

  return sessions;
}

/**
 * Threshold de near-duplicate em minutos.
 *
 * Nenhum colaborador legítimamente bate ponto duas vezes (mesma direção) num
 * intervalo curto. Casos típicos que geram near-dups:
 *   - REP-P retransmite a batida por falha de comunicação.
 *   - Colaborador esquece se já bateu e tenta de novo.
 *   - Colaborador marca saída duas vezes quando esqueceu a entrada
 *     (ex: 07:55 e 08:03 — 8 min de diferença).
 *
 * Reduzido de 15 → 12 min (bug C1 / cenário sintético #3 part-time 6h).
 * Pausa de exatamente 15 min é um padrão LEGÍTIMO comum em part-time
 * CLT (ex.: 13:00→16:00, pausa 15 min, 16:15→19:15 = jornada de 6h).
 * Com threshold 15 inclusivo, a 2ª batida (16:15) é descartada como
 * near-dup, sobram 3 batidas, pareamento fica imbalanced em 100% dos
 * casos. 12 min preserva a captura de duplicatas reais (REP-P retx,
 * dupla-saída por esquecimento) sem destruir pausa de 15min.
 */
const NEAR_DUP_THRESHOLD_MINUTES = 12;

/**
 * Remove marcações "near-duplicate" do mesmo colaborador antes do pareamento.
 *
 * Marcações REP-P offline (tipo 7) frequentemente registram a mesma batida de
 * ponto com timestamps separados por ≤ NEAR_DUP_THRESHOLD_MINUTES minutos (ex:
 * 11:03 e 11:04). Isso ocorre quando a batida é salva duas vezes no REP por
 * falha de comunicação ou re-transmissão. Sem deduplição, o algoritmo de
 * pareamento sequencial interpreta as duas marcações como entry+exit de um
 * intervalo curto, descartando o exit real (ex: 18:33) como imbalanced —
 * o que pode tanto subtrair quanto inflar o workedMinutes.
 *
 * Estratégia: para cada marcação, se a marcação IMEDIATAMENTE ANTERIOR (já
 * aceita) está dentro da janela de threshold, a nova marcação é descartada.
 * Mantemos a PRIMEIRA de cada cluster de near-dups, preservando a ordem
 * cronológica.
 *
 * @param punches Marcações já ordenadas por dateTime crescente.
 * @param thresholdMinutes Janela de dedup (padrão: NEAR_DUP_THRESHOLD_MINUTES = 15 min).
 */
function deduplicateNearDups(
  punches: CanonicalPunch[],
  thresholdMinutes = NEAR_DUP_THRESHOLD_MINUTES,
): CanonicalPunch[] {
  if (punches.length <= 1) return punches;

  const result: CanonicalPunch[] = [punches[0]!];

  for (let i = 1; i < punches.length; i++) {
    const current = punches[i]!;
    const prev = result[result.length - 1]!;

    const diffMin = diffInMinutes(current.dateTime, prev.dateTime);
    // diffMin pode ser negativo se datas cruzam meia-noite com operationalDate diferente;
    // nesse caso não são near-dups.
    if (diffMin >= 0 && diffMin <= thresholdMinutes) {
      // Descarta a marcação corrente — é near-dup da anterior.
      continue;
    }
    result.push(current);
  }

  return result;
}

/**
 * Limite máximo plausível de duração para um par entry→exit.
 *
 * 14h cobre o pior caso legítimo no Brasil: jornada 12×36 (12h contínuas
 * sem break registrado) + 2h de buffer pra slack de relógio. Pares com
 * duração > 14h são quase certamente artefato de pareamento errado
 * (ex: união de "saída da manhã" com "entrada da noite" no mesmo dia
 * operacional) e são reclassificados como imbalanced.
 *
 * Pares com duração negativa (entry > exit cronologicamente) também são
 * reclassificados — sinaliza erro no AFD ou no pareamento.
 */
const MAX_PLAUSIBLE_PAIR_HOURS = 14;

/**
 * Reclassifica pares implausíveis (>14h ou duração negativa) como imbalanced.
 * Os timestamps originais ficam preservados pra inspeção no breakdown,
 * mas `workedMinutes`/`saldo`/`atraso` ignoram esse pair.
 */
function clampImplausiblePairs(pairs: PunchPair[]): PunchPair[] {
  const limitMin = MAX_PLAUSIBLE_PAIR_HOURS * 60;
  return pairs.map((p) => {
    if (p.imbalanced) return p;
    if (p.entry === undefined || p.exit === undefined) return p;
    const dur = diffInMinutes(p.exit.dateTime, p.entry.dateTime);
    if (dur < 0 || dur > limitMin) {
      return { ...p, imbalanced: true };
    }
    return p;
  });
}

/**
 * Para marcações em ordem cronológica, alternando entry→exit heuristicamente.
 * Quando a direção é conhecida, usa-a diretamente.
 * Quando desconhecida, trata sequencialmente: 1º=entry, 2º=exit, 3º=entry, etc.
 *
 * Aplica deduplicação de near-dups (≤15 min) ANTES do pareamento para evitar
 * pares espúrios causados por marcações REP-P duplicadas.
 *
 * Após o pareamento, valida a duração de cada par: pares > 14h são
 * marcados como imbalanced (algoritmo `clampImplausiblePair`). Isso cobre
 * turnos noturnos legítimos (≤12h cruzando meia-noite) e descarta uniões
 * espúrias entre turnos distintos no mesmo dia operacional.
 */
function pairPunches(
  punches: CanonicalPunch[],
  employeeId: string,
  operationalDate: string,
): PunchPair[] {
  if (punches.length === 0) return [];

  // Deduplica near-dups antes do pareamento (protege contra re-transmissões REP-P).
  const deduped = deduplicateNearDups(punches);

  // Separar marcações com direção conhecida das desconhecidas
  const hasKnownDirection = deduped.some(p => p.direction !== 'unknown');

  const pairs: PunchPair[] = [];

  if (hasKnownDirection) {
    // Usar direção explícita: percorrer em ordem e consumir greedy
    const remaining = [...deduped];
    while (remaining.length > 0) {
      const first = remaining.shift();
      if (!first) break;

      if (first.direction === 'entry') {
        // Procura próximo exit
        const exitIdx = remaining.findIndex(p => p.direction === 'exit' || p.direction === 'unknown');
        if (exitIdx !== -1) {
          const exitPunch = remaining.splice(exitIdx, 1)[0];
          pairs.push({
            employeeId,
            operationalDate,
            entry: first,
            exit: exitPunch,
            imbalanced: false,
          });
        } else {
          // Entry sem exit
          pairs.push({
            employeeId,
            operationalDate,
            entry: first,
            exit: undefined,
            imbalanced: true,
          });
        }
      } else if (first.direction === 'exit') {
        // Exit sem entry anterior
        pairs.push({
          employeeId,
          operationalDate,
          entry: undefined,
          exit: first,
          imbalanced: true,
        });
      } else {
        // unknown: tratar como entry e tentar parear
        const exitIdx = remaining.findIndex(p => p.direction === 'exit' || p.direction === 'unknown');
        if (exitIdx !== -1) {
          const exitPunch = remaining.splice(exitIdx, 1)[0];
          pairs.push({
            employeeId,
            operationalDate,
            entry: first,
            exit: exitPunch,
            imbalanced: false,
          });
        } else {
          pairs.push({
            employeeId,
            operationalDate,
            entry: first,
            exit: undefined,
            imbalanced: true,
          });
        }
      }
    }
  } else {
    // Todas as direções são unknown: parear sequencialmente (1=entry, 2=exit, ...)
    for (let i = 0; i < deduped.length; i += 2) {
      const entry = deduped[i];
      const exit = deduped[i + 1];
      if (entry !== undefined && exit !== undefined) {
        pairs.push({
          employeeId,
          operationalDate,
          entry,
          exit,
          imbalanced: false,
        });
      } else if (entry !== undefined) {
        // Número ímpar: último entry sem exit
        pairs.push({
          employeeId,
          operationalDate,
          entry,
          exit: undefined,
          imbalanced: true,
        });
      }
    }
  }

  // Valida duração dos pares: > 14h ou negativo → imbalanced.
  return clampImplausiblePairs(pairs);
}

/**
 * Encontra a escala mais recente e vigente para o colaborador na data operacional.
 * Critério: effectiveStart ≤ operationalDate ≤ effectiveEnd (quando definidos).
 */
function findActiveSchedule(
  schedules: CanonicalSchedule[],
  employeeId: string,
  operationalDate: string,
): CanonicalSchedule | undefined {
  const candidates = schedules.filter(s => {
    if (s.employeeId !== employeeId) return false;
    if (s.effectiveStart !== undefined && operationalDate < s.effectiveStart) return false;
    if (s.effectiveEnd !== undefined && operationalDate > s.effectiveEnd) return false;
    return true;
  });

  if (candidates.length === 0) return undefined;

  // Pegar a mais recente (maior effectiveStart)
  return candidates.reduce((best, current) => {
    const bestStart = best.effectiveStart ?? '';
    const currentStart = current.effectiveStart ?? '';
    return currentStart > bestStart ? current : best;
  });
}

// =========================================================================
// Funções exportadas
// =========================================================================

/**
 * Calcula o total de minutos trabalhados somando todos os pares completos.
 * Pares sem entry ou sem exit são ignorados (imbalanced). Pares marcados
 * como imbalanced (mesmo com entry+exit, ex: duração > 14h reclassificada
 * por `clampImplausiblePair`) também são ignorados — duração é irreliável.
 */
export function calculateWorkedMinutes(pairs: PunchPair[]): number {
  let total = 0;
  for (const pair of pairs) {
    if (pair.imbalanced) continue;
    if (pair.entry !== undefined && pair.exit !== undefined) {
      const delta = diffInMinutes(pair.exit.dateTime, pair.entry.dateTime);
      if (delta > 0) total += delta;
    }
  }
  return total;
}

/**
 * Lê os minutos esperados da escala para um weekday específico.
 * Retorna 0 quando não há escala, o índice está fora do array, ou a data
 * é feriado nacional brasileiro (apenas feriados nacionais considerados).
 *
 * @param schedule Escala canônica do colaborador (ou undefined).
 * @param weekday Dia da semana 0..6 (domingo..sábado).
 * @param operationalDate Data operacional (AAAA-MM-DD) para verificação de feriado.
 */
export function calculateExpectedMinutes(
  schedule: CanonicalSchedule | undefined,
  weekday: number,
  operationalDate?: LocalDate,
): number {
  if (schedule === undefined) return 0;
  // Feriado nacional: zera a jornada esperada
  if (operationalDate !== undefined && isNationalHoliday(operationalDate)) {
    return 0;
  }
  const minutes = schedule.expectedMinutesByWeekday[weekday];
  return minutes ?? 0;
}

/**
 * Detecta pares com ausência de marcação (entry sem exit ou exit sem entry).
 * Retorna um sinal por par imbalanced encontrado.
 */
export function detectMissingPairs(journey: DailyJourney): JourneyIssueSignal[] {
  const signals: JourneyIssueSignal[] = [];
  for (const pair of journey.pairs) {
    if (pair.imbalanced) {
      signals.push({
        code: 'JOURNEY_MISSING_PUNCH',
        severity: 'warning',
        message: `Marcação incompleta na jornada de ${journey.operationalDate}: par sem entrada ou saída.`,
      });
    }
  }
  return signals;
}

/**
 * Retorna true quando a jornada tem ao menos um par com marcação faltando.
 * Nesses dias, NÃO sabemos o que faltou (entrada? saída? almoço?), então
 * cálculos de atraso/saída antecipada/excesso não são confiáveis e devem
 * ser ignorados — a jornada vai apenas pra "precisa de correção".
 */
function hasIncompletePunches(journey: DailyJourney): boolean {
  return journey.pairs.some((p) => p.imbalanced);
}

/**
 * Threshold de gap (minutos) entre pares consecutivos que sinaliza dado incompleto.
 *
 * 4 horas: uma pausa-almoço típica é 1–2h; um intervalo de turno raro chega a 3h.
 * Gap > 4h dentro do mesmo dia operacional é fortíssimo indicador de que falta
 * pelo menos um par de marcação entre os dois turnos registrados.
 *
 * NÃO confundir com SESSION_GAP_THRESHOLD_MINUTES (12h): aquele separa sessões
 * em dias distintos. Este opera DENTRO de uma sessão já detectada, verificando
 * se os pares internos têm gaps implausíveis para uma única jornada.
 */
const INCOMPLETE_GAP_THRESHOLD_MINUTES = 4 * 60;

/**
 * Threshold de proporção trabalhada/esperada abaixo do qual consideramos
 * que os dados são incompletos (Sinal X).
 *
 * 50% é conservador: mesmo em jornadas parcialmente registradas, se o colaborador
 * tiver marcado mais da metade do esperado, provavelmente o registro está correto.
 * Abaixo disso, com poucos pares, a chance de batidas faltando é alta.
 */
const INCOMPLETE_RATIO_THRESHOLD = 0.5;

/**
 * Limite de jornada (em minutos) acima do qual um par único é considerado
 * "esqueceu de bater o ponto do almoço". CLT art. 71 obriga 1h de pausa
 * intrajornada para jornadas > 6h — par único de >6h estatisticamente
 * indica que entry+exit do almoço não foram registrados.
 *
 * Estritamente >6h (= > 360 min): part-time legítimo de exatamente 6h
 * (par único 13:00→19:00) NÃO dispara este sinal, evitando falso positivo.
 */
const INCOMPLETE_SINGLE_PAIR_OVER_LUNCH_MINUTES = 6 * 60;

/**
 * Detecta se uma jornada tem dados parciais/incompletos (batidas faltando).
 *
 * Critério (X OR Y OR Z):
 * - Sinal X: ≤2 pares válidos (não-imbalanced) E workedMinutes < 50% do expectedMinutes.
 * - Sinal Y: gap entre o exit de um par e o entry do par seguinte > 4h.
 * - Sinal Z (Bug N5/N6): par único E workedMinutes > 6h. Caso típico:
 *   colaborador "esqueceu" de bater o par do almoço, gerando jornada bruta
 *   contínua de 9-10h que vira hora extra fictícia. CLT art. 71 obriga
 *   pausa intrajornada >1h em jornada >6h, então par único >6h é sinal
 *   forte de registro incompleto.
 *
 * Retorna false quando expectedMinutes = 0 (dia sem escala — não há referência).
 *
 * @param pairs Pares já calculados (resultado de pairPunches + clampImplausiblePairs).
 * @param workedMinutes Minutos trabalhados calculados (só pares completos).
 * @param expectedMinutes Minutos esperados da escala.
 * @param preAssignedLunchActive Quando true, Sinal Z é desativado: par único
 *   é regime LEGÍTIMO de almoço pré-assinalado (regulamentado por premissa
 *   do usuário). Sem esse opt-out, jornadas válidas viravam "incompletas"
 *   e o desconto nunca afetaria o saldo.
 */
export function detectIncompleteData(
  pairs: PunchPair[],
  workedMinutes: number,
  expectedMinutes: number,
  preAssignedLunchActive = false,
): boolean {
  // Sem escala: não há base pra comparar, não sinalizamos incompleto.
  if (expectedMinutes <= 0) return false;

  const validPairs = pairs.filter((p) => !p.imbalanced && p.entry !== undefined && p.exit !== undefined);

  // Sinal Z: par único E workedMinutes > 6h (almoço CLT esquecido).
  // Suprimido quando o usuário ativou almoço pré-assinalado — par único
  // é o padrão legítimo nesse regime.
  if (
    !preAssignedLunchActive &&
    validPairs.length === 1 &&
    workedMinutes > INCOMPLETE_SINGLE_PAIR_OVER_LUNCH_MINUTES
  ) {
    return true;
  }

  // Sinal X: ≤2 pares válidos E trabalhou menos que 50% do esperado.
  if (
    validPairs.length <= 2 &&
    workedMinutes < expectedMinutes * INCOMPLETE_RATIO_THRESHOLD
  ) {
    return true;
  }

  // Sinal Y: gap entre o exit de um par e o entry do par seguinte > 4h.
  // Verifica apenas pares consecutivos válidos (com entry e exit definidos).
  for (let i = 0; i < validPairs.length - 1; i++) {
    const currentExit = validPairs[i]!.exit;
    const nextEntry = validPairs[i + 1]!.entry;
    if (currentExit !== undefined && nextEntry !== undefined) {
      const gapMin = diffInMinutes(nextEntry.dateTime, currentExit.dateTime);
      if (gapMin > INCOMPLETE_GAP_THRESHOLD_MINUTES) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Detecta jornadas que terminaram mais curtas que a escala prevista.
 * Retorna o déficit apenas quando supera a tolerância, o dia é previsto e
 * o colaborador chegou a registrar pelo menos uma marcação válida.
 *
 * IMPORTANTE: este indicador é o critério único de "jornada mais curta".
 * As antigas funções `detectLateArrival` e `detectEarlyLeave` apuravam o
 * mesmo critério de déficit — sem horários de início/fim da escala não dá
 * para distinguir entrada tardia de saída antecipada.
 *
 * - Jornadas com marcações ímpares são ignoradas (par incompleto não diz
 *   o que faltou).
 * - Jornadas com workedMinutes = 0 são ignoradas (são faltas).
 * - Jornadas com `incompleteData` são ignoradas — o déficit reflete batidas
 *   faltando, não jornada curta real.
 */
export function detectShortJourney(
  journey: DailyJourney,
  toleranceMinutes: number,
): MinuteDelta | null {
  if (!journey.scheduledDay) return null;
  if (journey.workedMinutes === 0) return null;
  if (hasIncompletePunches(journey)) return null;
  if (journey.incompleteData) return null;
  const deficit = journey.expectedMinutes - journey.workedMinutes;
  if (deficit > toleranceMinutes) {
    return { minutes: deficit };
  }
  return null;
}

/**
 * Pré-computa a taxa de presença da empresa em cada operationalDate, usada
 * pela heurística de schedule sintético (quando o dataset não tem AEJ).
 *
 * Devolve `Map<LocalDate, number>` com o ratio `workers_no_dia / total_ativos`.
 *
 * Lógica do classificador (3 buckets):
 * - **≥ 80%**: "dia de trabalho da empresa" — esperado = jornada padrão
 *   (assumptions.dailyHours). Não trabalhar conta como falta.
 * - **< 20%**: "dia de descanso" — esperado = 0. Trabalhar conta como
 *   hora extra. Não trabalhar = OK, sem penalidade.
 * - **20–80%**: "dia possível" (ex: sábado parcial, dia entre feriados):
 *   - Se o colaborador trabalhou → esperado = jornada padrão (conta como
 *     dia normal pra ele, hora extra/atraso são calculados normalmente).
 *   - Se não trabalhou → esperado = 0, sem penalidade (afinal, a maioria
 *     dos colegas também não trabalhou).
 *
 * IMPORTANTE: usa sessões (detectJourneySessions) e não marcações brutas para
 * determinar o operationalDate de cada colaborador em cada dia. Isso evita o
 * spike de domingos causado por marcações de saída de turnos sábado→domingo,
 * cujo `punch.operationalDate` (definido pelo parser com cutoff de 4h) aponta
 * para domingo mesmo que a sessão pertença ao sábado. Usar sessões garante
 * consistência com a lógica de buildDailyJourneys.
 */
function computeCompanyAttendanceRates(
  dataset: CanonicalDataset,
): Map<LocalDate, number> {
  const totalActive = dataset.employees.filter((e) => e.active).length;
  if (totalActive === 0) return new Map();

  // Usa sessões (gap-based) para atribuir cada colaborador ao dia correto.
  // A session.operationalDate é dateOf(firstPunch.dateTime), o mesmo critério
  // usado por buildDailyJourneys — turnos noturnos que cruzam a meia-noite
  // ficam no dia em que COMEÇARAM, não no dia em que a saída foi registrada.
  const sessions = detectJourneySessions(dataset.punches);

  const workersByDate = new Map<LocalDate, Set<string>>();
  for (const session of sessions) {
    let set = workersByDate.get(session.operationalDate);
    if (set === undefined) {
      set = new Set();
      workersByDate.set(session.operationalDate, set);
    }
    set.add(session.employeeId);
  }

  const rates = new Map<LocalDate, number>();
  for (const [date, ids] of workersByDate) {
    rates.set(date, ids.size / totalActive);
  }
  return rates;
}

/** Limites do classificador de presença da empresa por dia. */
const ATTENDANCE_REST_THRESHOLD = 0.2;
const ATTENDANCE_WORK_THRESHOLD = 0.8;

/**
 * Mínimo de dias completos (sem marcações ímpares) com trabalho ≥1h para
 * considerar a inferência de jornada por colaborador. Abaixo disso a moda
 * é instável e cai-se no `standardDailyMinutes` global.
 */
const EMPLOYEE_JOURNEY_INFERENCE_MIN_SAMPLES = 5;

/**
 * Fração mínima de dias que precisam compartilhar a mesma jornada arredondada
 * para inferir a duração diária. Sinaliza "horário consistente" — ruído alto
 * (poucos dias na moda) significa que a empresa não tem rotina estável e o
 * fallback global é mais defensável.
 */
const EMPLOYEE_JOURNEY_INFERENCE_MIN_MODE_RATIO = 0.5;

/** Granularidade do bucket usado pra inferir a moda da jornada (em minutos). */
const EMPLOYEE_JOURNEY_BUCKET_MINUTES = 30;

/**
 * Jornadas "fora do grid de 30 min" que aparecem com tanta frequência no
 * mercado brasileiro que merecem bucket próprio — caso contrário a moda
 * cai num bucket de 30min vizinho e produz expected enganoso.
 *
 * - **524 min (8h44)**: 44h/semana ÷ 5 dias = 8.8h ≈ 8h48 puro, mas o
 *   formato comum de cartão-ponto é 8h44 (com 16 min "de tolerância" na
 *   chegada/saída descontados sem registrar). Bucket de 30min jogaria
 *   esse padrão pra 510 (8h30) ou 540 (9h), criando déficit ou overtime
 *   fictício de ~14-16 min/dia em colaboradores CLT 44h.
 *
 * Mantemos uma lista enxuta — adicionar buckets demais dilui o sinal
 * quando colaboradores genuinamente têm horários variados.
 */
const SPECIAL_SHIFT_BUCKETS_MINUTES: readonly number[] = [524];

/**
 * Mapeia uma amostra ao bucket mais próximo, considerando o grid padrão
 * de 30min E os buckets especiais. Vence a opção de menor distância da
 * amostra real.
 */
function bucketizeShift(sampleMinutes: number, gridMinutes: number): number {
  const gridBucket = Math.round(sampleMinutes / gridMinutes) * gridMinutes;
  let best = gridBucket;
  let bestDist = Math.abs(sampleMinutes - gridBucket);
  for (const special of SPECIAL_SHIFT_BUCKETS_MINUTES) {
    const dist = Math.abs(sampleMinutes - special);
    if (dist < bestDist) {
      best = special;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Infere a jornada diária TÍPICA de um colaborador a partir do padrão de
 * minutos trabalhados — usado SOMENTE em modo sintético (sem AEJ), quando
 * não temos a escala canônica.
 *
 * Premissa: se o colaborador trabalha consistentemente o mesmo número de
 * horas todo dia (ex.: 5h35, 6h, 5h56, 6h1...), o sinal mais provável é
 * que essa É a jornada real dele (part-time 6h), não que ele esteja
 * cronicamente atrasado vs uma jornada-padrão de 8h.
 *
 * Algoritmo:
 * 1. Detecta sessões do colaborador (gap-based).
 * 2. Filtra sessões com pares ímpares (não confiáveis) e com < 60 min
 *    trabalhados (provavelmente registro parcial).
 * 3. Arredonda cada amostra ao bucket de 30 min e calcula a moda.
 * 4. Retorna a moda quando: ≥5 amostras E ≥50% delas caem no bucket
 *    da moda. Caso contrário, retorna `null` (sem confiança).
 *
 * Bug N9: a inferência só substitui `standardDailyMinutes` PRA BAIXO
 * (jornada inferida < padrão = part-time legítimo) ou em buckets especiais
 * CLT (8h44 = 524 min). Pra cima, a moda 510 (8h30) num cenário de hora
 * extra moderada estrutural escondia o excesso — agora retornamos null
 * nesses casos, deixando o padrão 480 valer e o excesso aparecer.
 * EXCEÇÃO: plantão alternado (presença ≤65%) preserva moda mesmo >480.
 *
 * @param standardDailyMinutes Jornada padrão para comparar (default 480).
 * @returns minutos diários inferidos OU `null` quando não há sinal claro.
 */
export function inferEmployeeDailyMinutes(
  punches: CanonicalPunch[],
  employeeId: string,
  standardDailyMinutes: number = 480,
): number | null {
  // Cap CLT (bug A2): art. 59 limita hora extra a 2h/dia, então jornada
  // legítima máxima é 10h (= 600 min). Se a moda inferida ultrapassar isso,
  // não é "jornada típica" — é abuso de hora extra crônica que não deve
  // ser normalizado. Devolvemos null pra cair no `standardDailyMinutes`
  // global (480min) e o excesso aparecer no ranking.
  const MAX_INFERRED_DAILY_MINUTES = 600;

  const employeePunches = punches.filter((p) => p.employeeId === employeeId);
  if (employeePunches.length < EMPLOYEE_JOURNEY_INFERENCE_MIN_SAMPLES * 2) {
    return null;
  }

  const sessions = detectJourneySessions(employeePunches);
  const samples: number[] = [];

  for (const session of sessions) {
    const pairs = pairPunches(
      session.punches,
      session.employeeId,
      session.operationalDate,
    );
    if (pairs.some((p) => p.imbalanced)) continue;
    const worked = calculateWorkedMinutes(pairs);
    if (worked >= 60) samples.push(worked);
  }

  if (samples.length < EMPLOYEE_JOURNEY_INFERENCE_MIN_SAMPLES) return null;

  const bucket = EMPLOYEE_JOURNEY_BUCKET_MINUTES;
  const counts = new Map<number, number>();
  for (const s of samples) {
    const rounded = bucketizeShift(s, bucket);
    counts.set(rounded, (counts.get(rounded) ?? 0) + 1);
  }

  let mode = 0;
  let modeCount = 0;
  for (const [val, count] of counts) {
    if (count > modeCount || (count === modeCount && val > mode)) {
      mode = val;
      modeCount = count;
    }
  }

  if (modeCount / samples.length < EMPLOYEE_JOURNEY_INFERENCE_MIN_MODE_RATIO) {
    return null;
  }

  // Computa presença do colaborador no período (sessões / dias). Usado pra
  // detectar plantão alternado (12×36, 24×48): jornada longa + presença
  // esparsa = jornada contratada legítima, não overtime.
  const ALTERNATING_SHIFT_RATIO = 0.65;
  const sortedPunches = [...employeePunches].sort((a, b) =>
    a.dateTime < b.dateTime ? -1 : a.dateTime > b.dateTime ? 1 : 0,
  );
  const firstDt = sortedPunches[0]!.dateTime;
  const lastDt = sortedPunches[sortedPunches.length - 1]!.dateTime;
  const periodDays = Math.max(
    1,
    Math.round(
      (new Date(lastDt).getTime() - new Date(firstDt).getTime()) / 86_400_000,
    ) + 1,
  );
  const presenceRatio = sessions.length / periodDays;
  const isAlternatingShift = presenceRatio <= ALTERNATING_SHIFT_RATIO;
  const isSpecialBucket = SPECIAL_SHIFT_BUCKETS_MINUTES.includes(mode);

  // Cap CLT — não normalizar abuso de hora extra crônica.
  // EXCEÇÃO: regime de plantão alternado (12×36, 24×48). Esses
  // plantonistas combinam jornada longa (≥10h) com presença esparsa
  // no período (≤65% dos dias). Pra eles, a moda 720/1440 é a jornada
  // contratada — não pode ser zerada pelo cap. Sem essa exceção, todo
  // plantão legítimo viraria "240 min de excesso fictício por dia".
  if (mode > MAX_INFERRED_DAILY_MINUTES) {
    if (isAlternatingShift) {
      // Plantonista alternado — preserva moda como jornada legítima.
      return mode;
    }
    // Crônico (>65% de presença com >10h/dia) → cap CLT.
    return null;
  }

  // Bug N9: hora extra moderada estrutural (ex: 8h30/dia consistente) não
  // pode ser absorvida pela moda. Quando a inferência aponta jornada PRA
  // CIMA do padrão (mode > standardDailyMinutes) sem ser bucket especial
  // CLT (8h44 = 524) e o colaborador não é plantonista alternado, retornamos
  // null. Assim o standardDailyMinutes (geralmente 480) prevalece e o
  // overtime estrutural aparece nas métricas.
  if (mode > standardDailyMinutes && !isSpecialBucket && !isAlternatingShift) {
    return null;
  }

  return mode;
}

/**
 * Limite de presença por colaborador para considerar um weekday como parte
 * da escala individual do colaborador. Se ele trabalhou ≥50% das semanas
 * ativas naquele dia da semana, tratamos como dia previsto (não hora extra).
 *
 * Razão: heurística da empresa só cobre o caso "todo mundo trabalha sábado".
 * Para um colaborador específico que trabalha sábado consistente (ex.:
 * varejo, escala 6x1), a presença coletiva é baixa, mas individualmente
 * é parte da rotina — não deve inflar saldo positivo.
 */
const EMPLOYEE_WEEKDAY_PRESENCE_THRESHOLD = 0.5;
/** Mínimo de semanas ativas para a inferência por colaborador ser confiável. */
const EMPLOYEE_MIN_ACTIVE_WEEKS = 2;

/**
 * Chave de semana ISO (segunda-feira como início). Usada apenas como string
 * estável de agrupamento — não precisa bater com ISO 8601 oficial.
 */
function weekKey(date: LocalDate): string {
  const d = new Date(date + 'T00:00:00Z');
  // d.getUTCDay(): 0=Dom..6=Sáb. Convertemos pra 0=Seg..6=Dom para alinhar
  // segunda como início da semana.
  const dayMonFirst = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayMonFirst);
  return d.toISOString().slice(0, 10);
}

/**
 * Por colaborador, calcula a taxa de presença em cada weekday (0=Dom..6=Sáb)
 * sobre as semanas em que ele teve qualquer marcação. Permite identificar
 * uma escala individual (ex.: varejo trabalha sábado) mesmo quando a empresa
 * como um todo não trabalha aquele dia.
 *
 * Retorna `Map<employeeId, number[7]>` com taxas em [0, 1].
 */
function computePerEmployeeWeekdayPresence(
  dataset: CanonicalDataset,
): Map<string, number[]> {
  // Por employee: { activeWeeks: Set<week>, weekdayWeeks: Map<weekday, Set<week>> }
  const data = new Map<
    string,
    { activeWeeks: Set<string>; weekdayWeeks: Map<number, Set<string>> }
  >();

  for (const punch of dataset.punches) {
    const w = weekKey(punch.operationalDate);
    const wd = weekdayOf(punch.operationalDate);
    let rec = data.get(punch.employeeId);
    if (rec === undefined) {
      rec = { activeWeeks: new Set(), weekdayWeeks: new Map() };
      data.set(punch.employeeId, rec);
    }
    rec.activeWeeks.add(w);
    let set = rec.weekdayWeeks.get(wd);
    if (set === undefined) {
      set = new Set();
      rec.weekdayWeeks.set(wd, set);
    }
    set.add(w);
  }

  const result = new Map<string, number[]>();
  for (const [empId, rec] of data) {
    const total = rec.activeWeeks.size;
    if (total < EMPLOYEE_MIN_ACTIVE_WEEKS) {
      // Pouca evidência: assume todas as taxas como 0 (delega à heurística da empresa)
      result.set(empId, [0, 0, 0, 0, 0, 0, 0]);
      continue;
    }
    const rates = new Array(7).fill(0) as number[];
    for (let wd = 0; wd < 7; wd++) {
      const worked = rec.weekdayWeeks.get(wd)?.size ?? 0;
      rates[wd] = worked / total;
    }
    result.set(empId, rates);
  }
  return result;
}

/**
 * Mínimo de amostras por weekday pra inferência weekday-aware ser confiável.
 * Diferente de EMPLOYEE_JOURNEY_INFERENCE_MIN_SAMPLES (5) porque um mês de
 * dados só dá 4–5 sábados — usar 5 ali bloquearia inferência de qualquer dia
 * parcial. 2 amostras é o mínimo absoluto pra evitar uma única medição
 * outlier dominar.
 */
const EMPLOYEE_WEEKDAY_INFERENCE_MIN_SAMPLES = 2;
/** Razão mínima da moda nas amostras pra confiar na inferência por weekday. */
const EMPLOYEE_WEEKDAY_INFERENCE_MIN_MODE_RATIO = 0.5;

/**
 * Por colaborador, calcula MINUTOS médios trabalhados em cada weekday
 * (0=Dom..6=Sáb), considerando apenas sessões com pares balanceados.
 *
 * Permite detectar regimes como CLT 44h "8h seg-sex + 4h sábado" — onde
 * a presença de sábado é alta (≥50%), mas a jornada esperada é metade.
 * Sem isso, sábados de 4h viravam déficit de 4h fictício.
 *
 * Retorna `Map<employeeId, number[7]>` com minutos médios. 0 nos slots
 * sem amostras suficientes — chamador usa fallback pro `employeeDaily`.
 */
function computePerEmployeeWeekdayMinutes(
  dataset: CanonicalDataset,
  standardDailyMinutes: number = 480,
): Map<string, number[]> {
  const punchesByEmp = new Map<string, CanonicalPunch[]>();
  for (const p of dataset.punches) {
    let arr = punchesByEmp.get(p.employeeId);
    if (arr === undefined) {
      arr = [];
      punchesByEmp.set(p.employeeId, arr);
    }
    arr.push(p);
  }

  const bucket = EMPLOYEE_JOURNEY_BUCKET_MINUTES;
  const result = new Map<string, number[]>();

  for (const [empId, empPunches] of punchesByEmp) {
    const sessions = detectJourneySessions(empPunches);
    const buckets: number[][] = [[], [], [], [], [], [], []];
    for (const session of sessions) {
      const pairs = pairPunches(
        session.punches,
        session.employeeId,
        session.operationalDate,
      );
      if (pairs.some((p) => p.imbalanced)) continue;
      const worked = calculateWorkedMinutes(pairs);
      if (worked < 60) continue;
      const wd = weekdayOf(session.operationalDate);
      buckets[wd]!.push(worked);
    }

    const minutesByWd = new Array(7).fill(0) as number[];
    for (let wd = 0; wd < 7; wd++) {
      const samples = buckets[wd]!;
      if (samples.length < EMPLOYEE_WEEKDAY_INFERENCE_MIN_SAMPLES) continue;
      const counts = new Map<number, number>();
      for (const s of samples) {
        const rounded = bucketizeShift(s, bucket);
        counts.set(rounded, (counts.get(rounded) ?? 0) + 1);
      }
      let mode = 0;
      let modeCount = 0;
      for (const [val, count] of counts) {
        if (count > modeCount || (count === modeCount && val > mode)) {
          mode = val;
          modeCount = count;
        }
      }
      if (
        modeCount / samples.length >= EMPLOYEE_WEEKDAY_INFERENCE_MIN_MODE_RATIO
      ) {
        // Cap CLT (art. 59): mesma lógica do `inferEmployeeDailyMinutes`.
        // Moda > 10h é abuso crônico — não pode virar "jornada típica".
        if (mode > 600) continue;
        // Bug N9 (parity F4): inferência só substitui PRA BAIXO. Moda
        // acima do standardDaily só vence se for bucket especial CLT 44h
        // (524 = 8h44). Senão mantém fallback (=employeeDaily ou padrão)
        // pra que excesso estrutural seja detectado.
        const isSpecialBucket = SPECIAL_SHIFT_BUCKETS_MINUTES.includes(mode);
        if (mode > standardDailyMinutes && !isSpecialBucket) continue;
        minutesByWd[wd] = mode;
      }
    }
    result.set(empId, minutesByWd);
  }
  return result;
}

/**
 * Constrói jornadas diárias agrupando punches por employee+operationalDate,
 * pareando entry/exit, calculando workedMinutes vs expectedMinutes da escala.
 *
 * Quando o dataset não tem escalas reais (AEJ ausente, marcado em
 * `dataset.sources.schedulesAreSynthetic`), aplica heurística baseada
 * na presença da empresa por dia em vez do fallback rígido seg-sex.
 *
 * Retorna array vazio para inputs inválidos.
 */
export function buildDailyJourneys(
  dataset: CanonicalDataset,
  assumptions: DiagnosticAssumptions,
): DailyJourney[] {
  if (!dataset || !dataset.punches || dataset.punches.length === 0) return [];

  // Modo sintético: quando NÃO há escalas reais, infere o que é
  // dia de trabalho a partir do padrão de presença da empresa.
  const useSyntheticInference =
    dataset.sources?.schedulesAreSynthetic === true || dataset.schedules.length === 0;
  const attendanceRates = useSyntheticInference
    ? computeCompanyAttendanceRates(dataset)
    : new Map<LocalDate, number>();
  const employeeWeekdayPresence = useSyntheticInference
    ? computePerEmployeeWeekdayPresence(dataset)
    : new Map<string, number[]>();
  // Minutos médios por weekday (CLT 44h: 8h seg-sex + 4h sábado).
  // Slot=0 quando não há amostras suficientes — chamador usa fallback.
  const standardDailyMinutes = Math.round(
    (assumptions.dailyHours ?? 8) * 60,
  );
  const employeeWeekdayMinutes = useSyntheticInference
    ? computePerEmployeeWeekdayMinutes(dataset, standardDailyMinutes)
    : new Map<string, number[]>();
  // Em modo sintético, infere a jornada diária típica de cada colaborador
  // a partir do próprio padrão de trabalho. Quando há sinal forte (consistência
  // ≥50% num bucket de 30 min), substitui o `standardDailyMinutes` global —
  // evita marcar atraso crônico em quem na verdade tem jornada de 6h, 4h, etc.
  const employeeDailyMinutes = new Map<string, number>();
  // Almoço pré-assinalado: quando ativo, a inferência por moda observa
  // jornadas BRUTAS (sem desconto). Aplicamos o mesmo desconto na moda
  // inferida quando o colab usa majoritariamente padrão de 2 batidas
  // (1 par válido por sessão) — assim o expected reflete a jornada
  // CONTRATADA, não a bruta. Caso misto/4 batidas: sem ajuste.
  const preAssignedLunchGlobal = assumptions.preAssignedLunchMinutes ?? 0;
  if (useSyntheticInference) {
    for (const employee of dataset.employees) {
      const inferred = inferEmployeeDailyMinutes(
        dataset.punches,
        employee.identity.id,
        standardDailyMinutes,
      );
      if (inferred === null) continue;
      let adjusted = inferred;
      if (preAssignedLunchGlobal > 0) {
        const empPunches = dataset.punches.filter(
          (p) => p.employeeId === employee.identity.id,
        );
        const sessions = detectJourneySessions(empPunches);
        let onePairSessions = 0;
        for (const s of sessions) {
          const pairs = pairPunches(s.punches, s.employeeId, s.operationalDate);
          if (pairs.length === 1 && !pairs[0]!.imbalanced) onePairSessions++;
        }
        if (sessions.length > 0 && onePairSessions / sessions.length >= 0.5) {
          adjusted = Math.max(0, inferred - preAssignedLunchGlobal);
        }
      }
      employeeDailyMinutes.set(employee.identity.id, adjusted);
    }
  }

  // 1. Detectar sessões de trabalho (gap-based, cobre turnos noturnos
  //    que cruzam a meia-noite e se estendem além do cutoff de 4h).
  const sessions = detectJourneySessions(dataset.punches);

  const journeys: DailyJourney[] = [];

  for (const session of sessions) {
    const employeeId = session.employeeId;
    const operationalDate = session.operationalDate;

    // 2. Punches já vêm ordenados pela detectJourneySessions
    const sorted = session.punches;

    // 3. Parear entry/exit
    const pairs = pairPunches(sorted, employeeId, operationalDate);

    // 4. Calcular minutos trabalhados
    let workedMinutes = calculateWorkedMinutes(pairs);

    // 4.1. Almoço pré-assinalado: quando o colab tem só 1 par válido (entry+exit
    // únicas no dia, sem batidas de almoço) e o usuário declarou desconto via
    // premissas, subtrai esse valor da jornada bruta.
    //
    // Critério: pairs.length === 1 && !pairs[0].imbalanced. Empresas que JÁ
    // batem almoço (4 batidas → 2 pares) não são afetadas — preserva
    // retrocompatibilidade.
    const preAssignedLunch = assumptions.preAssignedLunchMinutes ?? 0;
    const isLunchPreAssigned =
      preAssignedLunch > 0 && pairs.length === 1 && !pairs[0]!.imbalanced;
    if (isLunchPreAssigned) {
      workedMinutes = Math.max(0, workedMinutes - preAssignedLunch);
    }

    // 5. Calcular minutos esperados:
    //    - Modo sintético: usa attendance da empresa pra classificar o dia.
    //    - Modo real (AEJ presente): usa a escala canônica.
    let expectedMinutes: number;
    if (useSyntheticInference) {
      if (isNationalHoliday(operationalDate)) {
        expectedMinutes = 0;
      } else {
        // Jornada diária do colaborador: inferida quando há sinal forte
        // (moda ≥50% num bucket de 30min); senão cai no padrão global.
        const employeeDaily =
          employeeDailyMinutes.get(employeeId) ?? standardDailyMinutes;
        const weekday = weekdayOf(operationalDate);
        const empPresence =
          employeeWeekdayPresence.get(employeeId)?.[weekday] ?? 0;
        // Minutos esperados ESPECÍFICOS pra esse weekday (=0 quando sem
        // confiança). Bug N2: CLT 44h "8h seg-sex + 4h sábado" — sábado
        // não pode usar expected=480 do "employeeDaily", senão vira déficit
        // de 4h fictício. Inferência weekday-aware retorna 240 nesse caso.
        const empWeekdayMin =
          employeeWeekdayMinutes.get(employeeId)?.[weekday] ?? 0;
        // Sinal individual forte: o colaborador trabalha esse weekday em
        // ≥50% das semanas ativas. Trata como escala dele, mesmo que a
        // empresa como um todo não trabalhe (ex.: varejo, escala 6x1).
        const isEmployeeScheduledWeekday =
          empPresence >= EMPLOYEE_WEEKDAY_PRESENCE_THRESHOLD;

        if (isEmployeeScheduledWeekday) {
          // Quando o colaborador trabalhou esse dia, é jornada normal;
          // quando não trabalhou (folga eventual), sem penalidade.
          // Preferência: usa minutos por weekday inferidos (≥2 amostras +
          // moda ≥50%); fallback pra `employeeDaily` quando não há sinal.
          const weekdayExpected = empWeekdayMin > 0 ? empWeekdayMin : employeeDaily;
          expectedMinutes = workedMinutes > 0 ? weekdayExpected : 0;
        } else {
          const rate = attendanceRates.get(operationalDate) ?? 0;
          if (rate >= ATTENDANCE_WORK_THRESHOLD) {
            // Dia de trabalho da empresa.
            expectedMinutes = employeeDaily;
          } else if (rate < ATTENDANCE_REST_THRESHOLD) {
            // Dia de descanso da empresa: trabalhar = hora extra,
            // não trabalhar = OK (sem falta).
            expectedMinutes = 0;
          } else {
            // Dia "possível" (zona ambígua 20-80%): a empresa não trabalha
            // em massa nesse dia E o colaborador também não tem padrão
            // individual claro (empPresence < 50%). Não dá pra cravar
            // expected=employeeDaily — isso geraria "jornada curta" fictícia
            // pra workers de plantão curto (ex.: 6h em domingo seria
            // marcado como -2h déficit vs jornada padrão 8h).
            //
            // Solução: expected = min(worked, employeeDaily). Significa:
            //  - Worker com 6h em dia ambíguo: expected=360, worked=360,
            //    deficit=0 (sem jornada curta).
            //  - Worker com 12h plantão em dia ambíguo: expected=480,
            //    worked=720, overtime=240 (overtime real preservado).
            //  - Worker que não bateu ponto: expected=0 (sem penalidade).
            expectedMinutes = workedMinutes > 0
              ? Math.min(workedMinutes, employeeDaily)
              : 0;
          }
        }
      }
    } else {
      const schedule = findActiveSchedule(dataset.schedules, employeeId, operationalDate);
      const weekday = weekdayOf(operationalDate);
      expectedMinutes = calculateExpectedMinutes(schedule, weekday, operationalDate);
    }

    // 6. Flags derivados
    const imbalanced = pairs.some(p => p.imbalanced);
    const scheduledDay = expectedMinutes > 0;

    // 7. crossesMidnight: qualquer marcação com data calendário diferente do operationalDate
    const crossesMidnight = sorted.some(p => dateOf(p.dateTime) !== operationalDate);

    // 8. Detectar dados incompletos (Sinal X ou Y; Z só quando NÃO há almoço
    // pré-assinalado — caso contrário par único de >6h é regime legítimo).
    const incompleteData = detectIncompleteData(
      pairs,
      workedMinutes,
      expectedMinutes,
      isLunchPreAssigned,
    );

    // 9. Coletar sinais de inconsistência
    const issues: JourneyIssueSignal[] = [];

    if (imbalanced) {
      issues.push({
        code: 'JOURNEY_MISSING_PUNCH',
        severity: 'warning',
        message: `Jornada de ${operationalDate} possui marcação incompleta (par sem entrada ou saída).`,
      });
    }

    if (incompleteData && !imbalanced) {
      issues.push({
        code: 'JOURNEY_INCOMPLETE_DATA',
        severity: 'warning',
        message: `Jornada de ${operationalDate} com padrão de dados incompletos: tempo registrado muito abaixo do esperado ou gap longo entre batidas. Déficit não contabilizado.`,
      });
    }

    if (!scheduledDay && sorted.length > 0) {
      issues.push({
        code: 'JOURNEY_NO_SCHEDULE',
        severity: 'info',
        message: `Jornada de ${operationalDate} não possui horário previsto em escala.`,
      });
    }

    if (
      scheduledDay &&
      workedMinutes > expectedMinutes + assumptions.toleranceMinutes
    ) {
      issues.push({
        code: 'JOURNEY_EXCEEDS_EXPECTED',
        severity: 'info',
        message: `Jornada de ${operationalDate} excede o previsto: ${workedMinutes} min trabalhados vs ${expectedMinutes} min esperados.`,
      });
    }

    if (
      scheduledDay &&
      workedMinutes < expectedMinutes - assumptions.toleranceMinutes
    ) {
      issues.push({
        code: 'JOURNEY_BELOW_EXPECTED',
        severity: 'warning',
        message: `Jornada de ${operationalDate} abaixo do previsto: ${workedMinutes} min trabalhados vs ${expectedMinutes} min esperados.`,
      });
    }

    journeys.push({
      employeeId,
      operationalDate,
      expectedMinutes,
      workedMinutes,
      pairs,
      issues,
      scheduledDay,
      crossesMidnight,
      incompleteData,
    });
  }

  // Ordenar por operationalDate + employeeId para output determinístico
  journeys.sort((a, b) => {
    const dateCompare = a.operationalDate < b.operationalDate ? -1 : a.operationalDate > b.operationalDate ? 1 : 0;
    if (dateCompare !== 0) return dateCompare;
    return a.employeeId < b.employeeId ? -1 : a.employeeId > b.employeeId ? 1 : 0;
  });

  return journeys;
}
